/**
 * SchedulingJobsWidget
 * ====================
 * Architecture (single-poller):
 *   - <SchedulingJobsPoller /> — renders nothing, just polls the API and writes
 *     to the Zustand store. Render it ONCE in DashboardLayout root.
 *   - <JobProgressOverlay />  — the floating bottom-right corner widget.
 *     Rendered by the Poller itself (always visible from any page).
 *   - <SchedulingJobsSidebar /> — the collapsible sidebar jobs list.
 *     Render in the sidebar section of DashboardLayout.
 */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, XCircle, RotateCcw, X, ChevronDown, Cpu, AlertTriangle } from 'lucide-react';
import { cn } from '../lib/utils';
import api from '../api';
import useCalendarStore from '../store/useCalendarStore';

// ── helpers ──────────────────────────────────────────────────────────────────

function fmtAge(iso) {
    if (!iso) return '';
    const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (secs < 60)  return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    return `${Math.floor(secs / 3600)}h ago`;
}

function statusColor(status) {
    return {
        running:   'text-violet-500',
        completed: 'text-emerald-500',
        cancelled: 'text-amber-500',
        failed:    'text-red-500',
        undone:    'text-zinc-400',
    }[status] || 'text-zinc-400';
}

function statusLabel(status) {
    return {
        running:   'Running',
        completed: 'Completed',
        cancelled: 'Cancelled',
        failed:    'Failed',
        undone:    'Undone',
    }[status] || status;
}

// ── Job actions (shared) ─────────────────────────────────────────────────────

function useJobActions(onRefresh) {
    const handleCancel = useCallback(async (jobId) => {
        try {
            const res = await api.post(`/api/scheduling/jobs/${jobId}/cancel`);
            if (res.data?.live === false) {
                toast.info('Job was stale (server restarted) — marked as cancelled.');
            } else {
                toast.info('Cancellation requested — reverting changes…');
            }
            onRefresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to cancel job');
        }
    }, [onRefresh]);

    const handleUndo = useCallback(async (jobId) => {
        try {
            const res = await api.post(`/api/scheduling/jobs/${jobId}/undo`);
            toast.success(`Undone — restored ${res.data.restored_events} previous events`);
            onRefresh();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to undo job');
        }
    }, [onRefresh]);

    return { handleCancel, handleUndo };
}

// ── Progress overlay (floating corner) ───────────────────────────────────────

function JobProgressOverlay({ job, onCancel }) {
    const [cancelling, setCancelling] = useState(false);
    if (!job) return null;

    const isRunning   = job.status === 'running';
    const isCompleted = job.status === 'completed';
    // Don't show overlay for old completed/cancelled/failed jobs
    if (!isRunning && !isCompleted) return null;

    const pct = isCompleted ? 100 : Math.min(90, Math.round((job.events_added || 0) / 2));

    const handleCancel = async () => {
        setCancelling(true);
        try { await onCancel(job.id); } finally { setCancelling(false); }
    };

    return (
        <div className="fixed bottom-6 right-6 z-[9999] pointer-events-auto">
            <div className={cn(
                "w-[340px] flex flex-col gap-3.5 p-5 rounded-2xl border shadow-2xl backdrop-blur-md transition-all duration-300",
                isCompleted
                    ? "border-emerald-200/50 dark:border-emerald-900/50 bg-white/95 dark:bg-zinc-900/95"
                    : "border-violet-200/50 dark:border-violet-900/50 bg-white/95 dark:bg-zinc-900/95"
            )}>
                {/* Header */}
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                        <div className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                            isCompleted ? "bg-emerald-100 dark:bg-emerald-900/30" : "bg-violet-100 dark:bg-violet-900/30"
                        )}>
                            {isCompleted
                                ? <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                                : <Loader2 className="w-4 h-4 animate-spin text-violet-600 dark:text-violet-400" />
                            }
                        </div>
                        <div>
                            <p className="text-[10px] font-black text-zinc-800 dark:text-zinc-100 uppercase tracking-wider leading-none">
                                {isCompleted ? 'Sync Completed' : 'AI Scheduler Running'}
                            </p>
                            <p className="text-[9px] text-zinc-400 dark:text-zinc-500 mt-0.5 leading-none">
                                {isCompleted
                                    ? 'Calendar updated successfully'
                                    : (cancelling ? 'Cancelling…' : 'Generating schedule, please wait')}
                            </p>
                        </div>
                    </div>
                    {isRunning && (
                        <button
                            onClick={handleCancel}
                            disabled={cancelling}
                            className={cn(
                                "flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-[9px] font-black uppercase tracking-wider transition-all",
                                cancelling
                                    ? "bg-zinc-100 dark:bg-zinc-800 text-zinc-400 cursor-not-allowed"
                                    : "bg-red-50 dark:bg-red-950/30 text-red-500 hover:bg-red-100 border border-red-200/60 dark:border-red-900/50"
                            )}
                        >
                            <X className="w-3 h-3" />
                            Stop
                        </button>
                    )}
                </div>

                {/* Progress bar */}
                <div className="space-y-1">
                    <div className="w-full h-1.5 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                        <div
                            className={cn(
                                "h-full rounded-full transition-all duration-700 ease-out",
                                isCompleted
                                    ? "bg-emerald-500"
                                    : "bg-gradient-to-r from-violet-500 via-indigo-500 to-purple-500"
                            )}
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                    <div className="flex justify-between text-[8px] text-zinc-400 font-bold uppercase tracking-wider">
                        <span>{pct}% complete</span>
                        <span>{isCompleted ? 'Done' : 'Do not close this tab'}</span>
                    </div>
                </div>

                {/* Stats footer */}
                <div className="pt-2.5 border-t border-zinc-100 dark:border-zinc-800/80 flex items-center justify-between">
                    <span className="text-[9px] text-zinc-500 font-bold uppercase tracking-wider">Events {isCompleted ? 'Added' : 'Generated'}</span>
                    <span className="text-[10px] font-black text-violet-600 dark:text-violet-400 bg-violet-500/5 px-2 py-0.5 rounded-full border border-violet-500/10">
                        {job.events_added || 0} events
                    </span>
                </div>
            </div>
        </div>
    );
}

// ── Individual job row ────────────────────────────────────────────────────────

function JobRow({ job, onUndo, onCancel }) {
    const [busy, setBusy] = useState(false);

    const act = (fn, jobId) => async () => {
        setBusy(true);
        try { await fn(jobId); } finally { setBusy(false); }
    };

    return (
        <div className="flex items-center gap-2.5 py-2 px-3 rounded-xl bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-100/80 dark:border-zinc-700/30">
            {/* Status icon */}
            <div className="shrink-0">
                {job.status === 'running'   && <Loader2      className="w-3.5 h-3.5 text-violet-500 animate-spin" />}
                {job.status === 'completed' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
                {job.status === 'cancelled' && <XCircle      className="w-3.5 h-3.5 text-amber-500" />}
                {job.status === 'failed'    && <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
                {job.status === 'undone'    && <RotateCcw    className="w-3.5 h-3.5 text-zinc-400" />}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
                <p className="text-[10px] font-black text-zinc-700 dark:text-zinc-200 leading-none truncate capitalize">
                    {job.type} Schedule
                </p>
                <p className="text-[9px] text-zinc-400 mt-0.5 leading-none">
                    <span className={cn("font-bold", statusColor(job.status))}>{statusLabel(job.status)}</span>
                    {job.events_added > 0 && ` · ${job.events_added} events`}
                    {job.started_at && ` · ${fmtAge(job.started_at)}`}
                </p>
            </div>

            {/* Actions */}
            <div className="shrink-0">
                {job.status === 'running' && (
                    <button
                        onClick={act(onCancel, job.id)}
                        disabled={busy}
                        className="h-6 px-2 rounded-lg text-[8px] font-black uppercase tracking-wider bg-red-50 dark:bg-red-950/30 text-red-500 hover:bg-red-100 border border-red-200/60 dark:border-red-900/50 transition-all disabled:opacity-50 flex items-center gap-1"
                    >
                        {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : 'Stop'}
                    </button>
                )}
                {job.status === 'completed' && (
                    <button
                        onClick={act(onUndo, job.id)}
                        disabled={busy}
                        className="h-6 px-2 rounded-lg text-[8px] font-black uppercase tracking-wider bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-600 border border-zinc-200 dark:border-zinc-600 transition-all disabled:opacity-50 flex items-center gap-1"
                    >
                        {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <><RotateCcw className="w-2.5 h-2.5" /> Undo</>}
                    </button>
                )}
            </div>
        </div>
    );
}

// ── Single Poller — renders once in DashboardLayout root ─────────────────────

export function SchedulingJobsPoller() {
    const schedulingJobs    = useCalendarStore(s => s.schedulingJobs);
    const activeJobId       = useCalendarStore(s => s.activeJobId);
    const setSchedulingJobs = useCalendarStore(s => s.setSchedulingJobs);
    const setActiveJobId    = useCalendarStore(s => s.setActiveJobId);

    // Completed-job overlay: show for 8 seconds after completion
    const [showCompletedOverlay, setShowCompletedOverlay] = useState(false);
    const completedTimerRef = useRef(null);
    const lastCompletedIdRef = useRef(null);

    const fetchJobs = useCallback(async () => {
        // Don't poll if tab is hidden or user is not logged in
        if (document.visibilityState === 'hidden') return;
        const token = localStorage.getItem('sb-access-token') ||
                      sessionStorage.getItem('sb-access-token') ||
                      (() => {
                          try {
                              const keys = Object.keys(localStorage).filter(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
                              if (keys.length > 0) return JSON.parse(localStorage.getItem(keys[0]))?.access_token;
                          } catch { return null; }
                      })();
        if (!token) return;  // Not logged in — skip

        try {
            const res = await api.get('/api/scheduling/jobs');
            const { jobs, active_job_id } = res.data;
            setSchedulingJobs(jobs || []);
            setActiveJobId(active_job_id || null);
        } catch {
            /* silent — don't spam errors on network hiccup */
        }
    }, [setSchedulingJobs, setActiveJobId]);

    // Adaptive polling: 2s when active, 60s when idle; paused when tab hidden
    const pollRef = useRef(null);
    useEffect(() => {
        fetchJobs();
        const ms = activeJobId ? 2000 : 60000;
        clearInterval(pollRef.current);
        pollRef.current = setInterval(fetchJobs, ms);

        const onVisibility = () => {
            if (document.visibilityState === 'visible') {
                fetchJobs();  // Immediately refresh when tab becomes visible
            }
        };
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            clearInterval(pollRef.current);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [activeJobId, fetchJobs]);

    // Track completed jobs for the temporary overlay
    useEffect(() => {
        const latestCompleted = schedulingJobs.find(j => j.status === 'completed');
        if (latestCompleted && latestCompleted.id !== lastCompletedIdRef.current && !activeJobId) {
            lastCompletedIdRef.current = latestCompleted.id;
            setShowCompletedOverlay(true);
            clearTimeout(completedTimerRef.current);
            completedTimerRef.current = setTimeout(() => setShowCompletedOverlay(false), 8000);
        }
    }, [schedulingJobs, activeJobId]);

    useEffect(() => () => {
        clearInterval(pollRef.current);
        clearTimeout(completedTimerRef.current);
    }, []);

    const { handleCancel } = useJobActions(fetchJobs);

    // Determine what job to show in the overlay
    const activeJob    = activeJobId ? schedulingJobs.find(j => j.id === activeJobId) : null;
    const completedJob = showCompletedOverlay ? schedulingJobs.find(j => j.status === 'completed') : null;
    const overlayJob   = activeJob || completedJob;

    return (
        <JobProgressOverlay job={overlayJob} onCancel={handleCancel} />
    );
}

// ── Sidebar section — reads from store, no polling ───────────────────────────

export function SchedulingJobsSidebar() {
    const schedulingJobs = useCalendarStore(s => s.schedulingJobs);
    const activeJobId    = useCalendarStore(s => s.activeJobId);
    const setSchedulingJobs = useCalendarStore(s => s.setSchedulingJobs);
    const setActiveJobId    = useCalendarStore(s => s.setActiveJobId);
    const [open, setOpen] = useState(true);

    const fetchJobs = useCallback(async () => {
        try {
            const res = await api.get('/api/scheduling/jobs');
            setSchedulingJobs(res.data.jobs || []);
            setActiveJobId(res.data.active_job_id || null);
        } catch { /* silent */ }
    }, [setSchedulingJobs, setActiveJobId]);

    const { handleCancel, handleUndo } = useJobActions(fetchJobs);

    if (schedulingJobs.length === 0) return null;

    return (
        <div className="mx-2 mb-2 rounded-2xl border border-zinc-200/60 dark:border-zinc-800/60 overflow-hidden bg-white/40 dark:bg-zinc-900/40 backdrop-blur-sm">
            <button
                onClick={() => setOpen(p => !p)}
                className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors"
            >
                <div className="flex items-center gap-2">
                    <Cpu className="w-3.5 h-3.5 text-violet-500" />
                    <span className="text-[9px] font-black uppercase tracking-widest text-zinc-500 dark:text-zinc-400">
                        Scheduler Jobs
                    </span>
                    {activeJobId && <span className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-pulse" />}
                </div>
                <ChevronDown className={cn("w-3.5 h-3.5 text-zinc-400 transition-transform duration-200", open && "rotate-180")} />
            </button>
            {open && (
                <div className="px-2 pb-2 space-y-1.5">
                    {schedulingJobs.slice(0, 8).map(job => (
                        <JobRow
                            key={job.id}
                            job={job}
                            onUndo={handleUndo}
                            onCancel={handleCancel}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

// Legacy default export (compatibility)
export default SchedulingJobsPoller;
